/**
 * Registros del borrador de una dispersión masiva (SPGRA-528).
 *
 * Puro: sin Angular. Es la regla de dinero de la revisión previa — lo que queda
 * en el borrador es exactamente lo que se envía al confirmar —, así que vive
 * aquí para probarse sin TestBed.
 */

/** Un renglón del borrador con un id que NO cambia aunque se eliminen otros. */
export interface RegistroMasivo<T> {
  readonly id: number;
  readonly datos: T;
}

/** Numera las filas en el orden del layout: id = posición, 1..n. */
export function numerarRegistros<T>(filas: readonly T[]): RegistroMasivo<T>[] {
  return filas.map((datos, indice) => ({ id: indice + 1, datos }));
}

/**
 * Devuelve un arreglo NUEVO sin los ids indicados, con el orden y los datos de
 * los demás intactos. Un id desconocido se ignora.
 */
export function quitarRegistros<T>(
  registros: readonly RegistroMasivo<T>[],
  ids: ReadonlySet<number>,
): RegistroMasivo<T>[] {
  return registros.filter(registro => !ids.has(registro.id));
}

/**
 * Suma en centavos ENTEROS para no arrastrar la deriva del punto flotante
 * (0.1 + 0.2). Un monto no numérico cuenta como 0.
 */
export function totalEnCentavos<T>(
  registros: readonly RegistroMasivo<T>[],
  monto: (datos: T) => number | string,
): number {
  return registros.reduce((acumulado, registro) => {
    const valor = Number(monto(registro.datos));
    return Number.isFinite(valor) ? acumulado + Math.round(valor * 100) : acumulado;
  }, 0);
}

/**
 * El borrador nunca se vacía por esta vía: para descartarlo todo están
 * "Cancelar" y quitar el archivo, que ya limpian el borrador.
 */
export function dejaAlMenosUno(totalRegistros: number, aQuitar: number): boolean {
  return totalRegistros - aQuitar >= 1;
}
